import { useState, useRef, useEffect } from 'react';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const IMGBB_API_KEY = process.env.REACT_APP_IMGBB_API_KEY;
const IMGBB_UPLOAD_URL = 'https://api.imgbb.com/1/upload';
// 4-wide caused real failures (12+ image batches losing a handful mid-batch)
// so this dropped to 1 (sequential) in 1.13.1. Re-tested at 2-wide with a
// 20-image batch and no failures, ~20% faster wall-clock than sequential —
// the gain is modest because two uploads share one uplink's bandwidth rather
// than getting independent pipes. If failures resurface in real usage, drop
// this back to 1 rather than pushing it higher; 4 is the known-bad number.
const UPLOAD_CONCURRENCY = 2;
const GREEN = '#4a9b6f';

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result.split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

async function uploadToImgbb(file, name) {
  const base64 = await fileToBase64(file);
  const body = new URLSearchParams();
  body.set('image', base64);
  if (name) body.set('name', name);
  const res = await fetch(`${IMGBB_UPLOAD_URL}?key=${IMGBB_API_KEY}`, {
    method: 'POST',
    body,
  });
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data?.error?.message || 'Upload failed');
  return data.data.url;
}

// Formats a file's lastModified time (when the photo was actually taken,
// not when it was uploaded) as e.g. 2026-10-18_t14-15. Upload time isn't
// useful here since an entire batch uploads within the same few seconds —
// capture time is what actually differs from one photo to the next.
const formatCaptureTime = (file) => {
  const d = new Date(file.lastModified || Date.now());
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_t${pad(d.getHours())}-${pad(d.getMinutes())}`;
};

// Recursively walks dropped DataTransferItems, following directories, and
// returns every image file found (what lets a whole dropped folder work).
async function filesFromDataTransferItems(dtItems) {
  // Browsers invalidate a DataTransferItem's underlying data as soon as the
  // drop handler yields control (i.e. the first `await`), even for items we
  // haven't looked at yet. So every entry/file reference must be grabbed
  // synchronously, in one pass, before any async work (directory reads,
  // entry.file()) begins — otherwise everything after the first item that
  // triggers an await silently comes back null and gets dropped.
  const refs = dtItems.map(item => ({
    entry: item.webkitGetAsEntry ? item.webkitGetAsEntry() : null,
    file: item.getAsFile ? item.getAsFile() : null,
  }));

  const files = [];
  const walk = (entry) => new Promise((resolve) => {
    if (!entry) return resolve();
    if (entry.isFile) {
      entry.file(file => { if (file.type.startsWith('image/')) files.push(file); resolve(); }, resolve);
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      // readEntries() can return results in batches (directories with many
      // files) — keep calling until it returns an empty array.
      const readNextBatch = () => {
        reader.readEntries(async entries => {
          if (entries.length === 0) return resolve();
          for (const e of entries) await walk(e);
          readNextBatch();
        }, resolve);
      };
      readNextBatch();
    } else {
      resolve();
    }
  });

  for (const { entry, file } of refs) {
    if (entry) {
      await walk(entry);
    } else if (file && file.type.startsWith('image/')) {
      files.push(file);
    }
  }
  return files;
}

const filenameSortKey = (name) => {
  const num = parseInt((name.match(/\d+/) || ['0'])[0], 10);
  return [num, name];
};

const sortItemsByFilename = (items) => [...items].sort((a, b) => {
  const [numA, nameA] = filenameSortKey(a.file.name);
  const [numB, nameB] = filenameSortKey(b.file.name);
  return numA - numB || nameA.localeCompare(nameB);
});

let nextItemId = 1;
const makeItem = (file) => ({
  id: nextItemId++,
  file,
  previewUrl: URL.createObjectURL(file),
  status: 'pending', // pending | uploading | done | error
  url: null,
  error: null,
});

const UPLOAD_SIDEBAR_STEPS = [
  { title: 'Photograph your cards', body: 'Front and back of each card, one after another. Well-lit, filling most of the frame.' },
  { title: 'Drop the whole batch in', body: 'A whole folder, or several photos at once — no need to add cards one at a time.' },
  { title: 'We handle the rest', body: 'Photos are sorted by filename and paired front, back, front, back automatically. Drag a thumbnail to fix any mix-ups.' },
];

const PASTE_SIDEBAR_STEPS = [
  { title: 'Photograph your cards', body: 'Front and back of each card, well-lit, filling most of the frame.' },
  { title: 'Upload to a free image host', body: 'Any host works — Postimages and Photobucket are good options. Copy the direct image URL (ends in .jpg or .png).' },
  { title: 'Paste the URLs here', body: 'One per line, alternating front, back, front, back for each card.' },
];

function Sidebar({ mode, text, text2, text3, bg2, border, isMobile }) {
  const headline = mode === 'paste' ? 'Add your image links' : 'Add your card photos';
  const stepList = mode === 'paste' ? PASTE_SIDEBAR_STEPS : UPLOAD_SIDEBAR_STEPS;
  return (
    <div style={isMobile
      ? { padding: '20px 20px 16px', background: bg2, borderBottom: `1px solid ${border}` }
      : { flex: '1 1 280px', maxWidth: 340, minWidth: 260, padding: '36px 32px', background: bg2, borderRight: `1px solid ${border}` }}>
      <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, letterSpacing: '0.08em', color: text3, fontWeight: 500 }}>HOW IT WORKS</div>
      <div style={{ fontSize: isMobile ? 17 : 21, fontWeight: 600, color: text, marginTop: 8, marginBottom: isMobile ? 16 : 26, lineHeight: 1.3 }}>{headline}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 14 : 22 }}>
        {stepList.map((s, i) => (
          <div key={s.title} style={{ display: 'flex', gap: 14 }}>
            <div style={{ flexShrink: 0, width: 24, height: 24, borderRadius: '50%', background: bg2, border: `1.5px solid ${border}`, color: text2, fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {i + 1}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: text, marginBottom: 4 }}>{s.title}</div>
              <div style={{ fontSize: 12, color: text2, lineHeight: 1.6 }}>{s.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModeToggle({ mode, setMode, uploading, text, text2, bg, bg2, border }) {
  const btnStyle = (active) => ({
    padding: '9px 20px',
    borderRadius: 999,
    border: 'none',
    fontSize: 12,
    fontWeight: 500,
    fontFamily: 'IBM Plex Mono, monospace',
    cursor: uploading ? 'default' : 'pointer',
    background: active ? text : 'transparent',
    color: active ? bg : text2,
    transition: 'background 0.15s, color 0.15s',
    whiteSpace: 'nowrap',
  });
  return (
    <div style={{ display: 'inline-flex', background: bg2, borderRadius: 999, padding: 4, border: `1px solid ${border}`, flexShrink: 0 }}>
      <button onClick={() => setMode('upload')} disabled={uploading} style={btnStyle(mode === 'upload')}>Upload Photos</button>
      <button onClick={() => setMode('paste')} disabled={uploading} style={btnStyle(mode === 'paste')}>Paste URLs</button>
    </div>
  );
}

function Thumb({ item, index, label, dragging, onDragStart, onDragOver, onDrop, onRemove, warn, locked, border, text, text2 }) {
  return (
    <div
      draggable={!locked}
      onDragStart={() => onDragStart(index)}
      onDragOver={e => { e.preventDefault(); onDragOver(index); }}
      onDrop={e => { e.preventDefault(); onDrop(index); }}
      style={{
        width: 96,
        height: 96,
        borderRadius: 8,
        border: `1.5px solid ${warn ? '#c0453e' : border}`,
        position: 'relative',
        overflow: 'hidden',
        cursor: locked ? 'default' : 'grab',
        opacity: dragging ? 0.4 : 1,
        flexShrink: 0,
      }}>
      <img src={item.previewUrl} alt={label} draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', textAlign: 'center', padding: '3px 0' }}>
        {label}
      </div>
      {!locked && (
        <button
          onClick={e => { e.stopPropagation(); onRemove(item.id); }}
          title="Remove"
          style={{ position: 'absolute', top: 3, right: 3, width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', fontSize: 12, lineHeight: '18px', padding: 0, cursor: 'pointer' }}>
          ×
        </button>
      )}
      {item.status === 'uploading' && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontFamily: 'IBM Plex Mono, monospace', textAlign: 'center' }}>
          Uploading…
        </div>
      )}
      {item.status === 'error' && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(180,40,40,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontFamily: 'IBM Plex Mono, monospace', textAlign: 'center', padding: 4 }}>
          Failed
        </div>
      )}
      {item.status === 'done' && (
        <div style={{ position: 'absolute', top: 3, left: 3, width: 16, height: 16, borderRadius: '50%', background: GREEN, color: '#fff', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>
      )}
    </div>
  );
}

function Modal({ theme, onStart, onClose }) {
  // 'upload' = drop/select files directly; 'paste' = the original paste-a-URL
  // flow, kept as a fallback while the upload path is still being tested.
  const [mode, setMode] = useState('upload');
  const [items, setItems] = useState([]);
  const [dragOverZone, setDragOverZone] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadedCount, setUploadedCount] = useState(0);
  const [pasteUrls, setPasteUrls] = useState('');
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const isMobile = useIsMobile();

  const border = theme === 'dark' ? '#2e2c28' : '#dddad4';
  const bg = theme === 'dark' ? '#141412' : '#f7f6f3';
  const bg2 = theme === 'dark' ? '#1c1b18' : '#eeecea';
  const text = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2 = theme === 'dark' ? '#857f76' : '#6b6760';
  const text3 = theme === 'dark' ? '#4a4640' : '#a8a49e';

  // Upload progress lives only in this component's state — nothing is
  // persisted until every image has uploaded and onStart() hands the URLs
  // off to the parent. Warn before an accidental tab close/refresh loses it.
  useEffect(() => {
    if (!uploading) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploading]);

  // Same risk via the in-app close paths (Cancel button, clicking the
  // backdrop) — confirm instead of silently abandoning in-flight uploads.
  const handleClose = () => {
    if (uploading && !window.confirm('Uploads are still in progress — leaving now will lose them. Cancel anyway?')) return;
    onClose();
  };

  const pairCount = Math.floor(items.length / 2);
  const hasOddLeftover = items.length % 2 !== 0;

  // Sorted by filename automatically — camera photos are almost always
  // sequential (IMG_001, IMG_002...), and browsers/OSes don't guarantee
  // drop/selection order matches that. "Sort by filename" stays available
  // as a manual re-sort (e.g. after a manual drag-reorder, or after adding
  // more files from a different source with its own numbering).
  const addFiles = (files) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    setItems(prev => sortItemsByFilename([...prev, ...imageFiles.map(makeItem)]));
  };

  const handleZoneDrop = async (e) => {
    e.preventDefault();
    setDragOverZone(false);
    if (uploading) return;
    const dtItems = Array.from(e.dataTransfer.items || []);
    if (dtItems.length && dtItems[0].webkitGetAsEntry) {
      addFiles(await filesFromDataTransferItems(dtItems));
    } else {
      addFiles(Array.from(e.dataTransfer.files || []));
    }
  };

  // Revoke every blob preview URL on unmount (modal closed/cards started)
  // so a session with several drop-add-start cycles doesn't leak memory.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => () => { itemsRef.current.forEach(i => URL.revokeObjectURL(i.previewUrl)); }, []);

  const removeItem = (id) => setItems(prev => {
    const removed = prev.find(i => i.id === id);
    if (removed) URL.revokeObjectURL(removed.previewUrl);
    return prev.filter(i => i.id !== id);
  });

  const clearAll = () => {
    items.forEach(i => URL.revokeObjectURL(i.previewUrl));
    setItems([]);
  };

  const sortByFilename = () => setItems(sortItemsByFilename);

  const handleThumbDrop = (toIndex) => {
    if (dragIndex === null || dragIndex === toIndex) { setDragIndex(null); return; }
    setItems(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setDragIndex(null);
  };

  const totalToUpload = pairCount * 2;
  // UPLOAD_CONCURRENCY can be >1, so more than one item may be 'uploading'
  // at once — list every one of them rather than assuming just the first.
  const uploadingLabels = items
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => item.status === 'uploading')
    .map(({ idx }) => `card ${Math.floor(idx / 2) + 1} · ${idx % 2 === 0 ? 'front' : 'back'}`);
  const uploadingLabel = uploadingLabels.join(', ');
  const progressPct = totalToUpload > 0 ? Math.round((uploadedCount / totalToUpload) * 100) : 0;

  const handleStartClick = async () => {
    if (pairCount === 0 || hasOddLeftover || uploading) return;
    setUploading(true);
    setUploadError('');
    setUploadedCount(items.filter(i => i.status === 'done').length);

    const working = items.slice(0, pairCount * 2);
    let cursor = 0;
    let failed = false;

    const setItemStatus = (id, patch) => {
      setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));
    };

    const worker = async () => {
      while (cursor < working.length) {
        const idx = cursor++;
        const item = working[idx];
        if (item.url) continue;
        setItemStatus(item.id, { status: 'uploading', error: null });
        try {
          const cardNum = Math.floor(idx / 2) + 1;
          const side = idx % 2 === 0 ? 'front' : 'back';
          const name = `card${cardNum}-${side}-${formatCaptureTime(item.file)}`;
          const url = await uploadToImgbb(item.file, name);
          item.url = url;
          setItemStatus(item.id, { status: 'done', url });
          setUploadedCount(c => c + 1);
        } catch (err) {
          failed = true;
          setItemStatus(item.id, { status: 'error', error: err.message });
        }
      }
    };

    await Promise.all(Array.from({ length: UPLOAD_CONCURRENCY }, worker));

    setUploading(false);
    if (failed) {
      setUploadError('Some images failed to upload. Fix the highlighted thumbnails and click Start processing again.');
      return;
    }
    onStart(working.map(i => i.url));
  };

  const pasteLines = pasteUrls.split('\n').filter(l => l.trim().startsWith('http'));
  const pasteCardCount = Math.floor(pasteLines.length / 2);

  const sortPasteByFilename = () => {
    const sorted = pasteUrls.split('\n')
      .filter(l => l.trim())
      .sort((a, b) => {
        const numA = parseInt((a.split('/').pop().match(/\d+/) || ['0'])[0], 10);
        const numB = parseInt((b.split('/').pop().match(/\d+/) || ['0'])[0], 10);
        return numA - numB;
      });
    setPasteUrls(sorted.join('\n'));
  };

  const handlePasteStart = () => {
    if (pasteCardCount === 0) return;
    onStart(pasteLines);
  };

  return (
    <div onClick={handleClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center', zIndex: 100, backdropFilter: 'blur(2px)' }}>
      <div
        onClick={e => e.stopPropagation()}
        style={isMobile ? {
          background: bg, border: `1px solid ${border}`, borderRadius: 0,
          width: '100vw', maxHeight: '100vh', height: '100%',
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
        } : {
          background: bg, border: `1px solid ${border}`, borderRadius: 16,
          width: '75vw', maxWidth: 1200, minWidth: 340, maxHeight: '85vh',
          overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.28)',
          display: 'flex', flexWrap: 'wrap',
        }}>

        <Sidebar mode={mode} text={text} text2={text2} text3={text3} bg2={bg2} border={border} isMobile={isMobile} />

        <div style={isMobile
          ? { flex: '1 1 auto', minWidth: 0, overflowY: 'auto', padding: 20 }
          : { flex: '3 1 480px', minWidth: 360, maxHeight: '85vh', overflowY: 'auto', padding: 36 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 20, fontWeight: 600, color: text }}>{mode === 'paste' ? 'Paste image URLs' : 'Drop card images'}</div>
            <ModeToggle mode={mode} setMode={setMode} uploading={uploading} text={text} text2={text2} bg={bg} bg2={bg2} border={border} />
          </div>
          <div style={{ fontSize: 13, color: text2, marginBottom: 24, lineHeight: 1.5 }}>
            {mode === 'paste'
              ? 'Add one URL per line, alternating front and back for each card.'
              : 'Drop a folder or several photos at once. Order matters — images are paired front, back, front, back for each card, in the order shown below.'}
          </div>

          {mode === 'paste' ? (
            <>
              <textarea
                value={pasteUrls}
                onChange={e => setPasteUrls(e.target.value)}
                placeholder={'https://i.postimg.cc/abc123/front.jpg\nhttps://i.postimg.cc/def456/back.jpg'}
                style={{ width: '100%', height: 220, background: bg2, border: `1px solid ${border}`, borderRadius: 8, padding: 14, fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, color: text, resize: 'none', outline: 'none', lineHeight: 1.6, boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12 }}>
                <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: text3 }}>
                  {pasteLines.length} URL{pasteLines.length !== 1 ? 's' : ''} · {pasteCardCount} card{pasteCardCount !== 1 ? 's' : ''}
                </div>
                {pasteLines.length > 1 && (
                  <button onClick={sortPasteByFilename} style={{ background: 'transparent', border: 'none', fontSize: 12, color: text2, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontFamily: 'inherit' }}>Sort by filename</button>
                )}
              </div>
            </>
          ) : (
            <>
              <div
                onClick={() => !uploading && fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); if (!uploading) setDragOverZone(true); }}
                onDragLeave={() => setDragOverZone(false)}
                onDrop={handleZoneDrop}
                style={{
                  border: `2px dashed ${dragOverZone ? GREEN : border}`,
                  background: dragOverZone ? bg2 : 'transparent',
                  borderRadius: 10,
                  padding: 40,
                  textAlign: 'center',
                  cursor: uploading ? 'default' : 'pointer',
                  opacity: uploading ? 0.5 : 1,
                  transition: 'border-color 0.15s, background 0.15s',
                }}>
                <div style={{ fontSize: 30, marginBottom: 10, color: dragOverZone ? GREEN : text2 }}>⬆</div>
                <div style={{ fontSize: 15, color: text, fontWeight: 500 }}>Drop a folder or photos here</div>
                <div style={{ fontSize: 12, color: text2, marginTop: 4 }}>or click to browse your files</div>
                <div style={{ fontSize: 11, color: text3, marginTop: 12 }}>On mobile, tap to pick several photos from your camera roll</div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={e => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
                  style={{ display: 'none' }}
                />
                <input
                  ref={folderInputRef}
                  type="file"
                  /* eslint-disable-next-line react/no-unknown-property */
                  webkitdirectory=""
                  multiple
                  onChange={e => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
                  style={{ display: 'none' }}
                />
              </div>
              <button
                onClick={e => { e.stopPropagation(); folderInputRef.current?.click(); }}
                disabled={uploading}
                style={{ marginTop: 8, background: 'transparent', border: 'none', fontSize: 12, color: text2, cursor: uploading ? 'default' : 'pointer', textDecoration: 'underline', padding: 0, opacity: uploading ? 0.5 : 1 }}>
                or select an entire folder
              </button>

              {items.length > 0 && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, marginBottom: 10 }}>
                    <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: text3 }}>{uploading ? 'Uploading…' : 'Drag a thumbnail to reorder'}</div>
                    {!uploading && (
                      <div style={{ display: 'flex', gap: 14 }}>
                        <button onClick={sortByFilename} style={{ background: 'transparent', border: 'none', fontSize: 12, color: text2, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>Sort by filename</button>
                        <button onClick={clearAll} style={{ background: 'transparent', border: 'none', fontSize: 12, color: text2, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>Clear all</button>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {items.map((item, i) => (
                      <Thumb
                        key={item.id}
                        item={item}
                        index={i}
                        label={`${Math.floor(i / 2) + 1} · ${i % 2 === 0 ? 'Front' : 'Back'}`}
                        dragging={dragIndex === i}
                        onDragStart={setDragIndex}
                        onDragOver={() => {}}
                        onDrop={handleThumbDrop}
                        onRemove={removeItem}
                        warn={hasOddLeftover && i === items.length - 1}
                        locked={uploading}
                        border={border}
                        text={text}
                        text2={text2}
                      />
                    ))}
                  </div>
                  {hasOddLeftover && (
                    <div style={{ marginTop: 12, fontSize: 12, color: '#c0453e', fontFamily: 'IBM Plex Mono, monospace' }}>
                      ⚠ {items.length} images is an odd number — the last one is missing its pair. Add its match or remove it.
                    </div>
                  )}
                </>
              )}

              {uploading && (
                <div style={{ marginTop: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: text2, marginBottom: 8, fontFamily: 'IBM Plex Mono, monospace' }}>
                    <span>{uploadingLabel ? `Uploading ${uploadingLabel}…` : 'Uploading…'}</span>
                    <span>{uploadedCount}/{totalToUpload} · {progressPct}%</span>
                  </div>
                  <div style={{ height: 8, background: bg2, borderRadius: 4, overflow: 'hidden', border: `1px solid ${border}` }}>
                    <div style={{ height: '100%', width: `${progressPct}%`, background: GREEN, borderRadius: 4, transition: 'width 0.3s ease' }} />
                  </div>
                </div>
              )}
            </>
          )}

          {uploadError && (
            <div style={{ marginTop: 16, fontSize: 12, color: '#c0453e', fontFamily: 'IBM Plex Mono, monospace' }}>{uploadError}</div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 28, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, color: text3 }}>
              {mode === 'paste'
                ? `${pasteCardCount} card${pasteCardCount !== 1 ? 's' : ''} ready`
                : (uploading ? `Uploading ${uploadedCount}/${totalToUpload}…` : `${pairCount} card${pairCount !== 1 ? 's' : ''} ready`)}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleClose} style={{ padding: '11px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 6, fontSize: 13, color: text2, cursor: 'pointer' }}>Cancel</button>
              {mode === 'paste' ? (
                <button
                  onClick={handlePasteStart}
                  disabled={pasteCardCount === 0}
                  style={{ padding: '11px 24px', background: text, border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, color: bg, cursor: pasteCardCount === 0 ? 'default' : 'pointer', opacity: pasteCardCount === 0 ? 0.5 : 1 }}>
                  Start processing →
                </button>
              ) : (
                <button
                  onClick={handleStartClick}
                  disabled={pairCount === 0 || hasOddLeftover || uploading}
                  style={{ padding: '11px 24px', background: text, border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, color: bg, cursor: (pairCount === 0 || hasOddLeftover || uploading) ? 'default' : 'pointer', opacity: (pairCount === 0 || hasOddLeftover || uploading) ? 0.5 : 1 }}>
                  {uploading ? 'Uploading…' : 'Start processing →'}
                </button>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default Modal;
